import axios from 'axios';

import config from '../../config/config';
import getFirstSubdomain from '../../shared-functions/DomiansplitFunction';

export interface BrandingData {
    shortName: string;
    clientName: string;
    logo: string;
    favicon: string;
    theme: any;
    footer: any;
    branding?: string; // energic | voltie
    description?: string;
    logoUrl?: string;
    webSocketUrl?: string;
    guestPaymentMethod?: string;
    stripePublishableKey?: string;
    hyperPayScriptUrl?: string;
    transactionApiUrl?: string;
    currencyCode?: string;
}

const STORAGE_KEY_PREFIX = 'dynamicBranding_';

/**
 * Service to handle dynamic branding operations
 */
const BrandingService = {
    /**
     * Gets the storage key for the current domain
     * @returns {string}
     */
    getStorageKey: (): string => {
        return `${STORAGE_KEY_PREFIX}${getFirstSubdomain() || 'default'}`;
    },
    /**
     * Fetches branding configuration for the current domain.
     * @returns {Promise<BrandingData | null>} The fetched branding data.
     */
    fetchBranding: async (): Promise<BrandingData | null> => {
        return BrandingService.fetchClientConfig();
    },

    /**
     * Fetches branding configuration for the current domain.
     * @returns {Promise<BrandingData | null>} The fetched branding data.
     */
    fetchClientConfig: async (): Promise<BrandingData | null> => {
        const domain = getFirstSubdomain() || '';
        if (!domain) return null;

        // Check cache first
        const cached = BrandingService.getCachedBranding();
        if (cached) {
            return cached;
        }

        try {
            const response = await axios.get(
                `${config.commonApiUrl}api/saas-client-config/v1/client-config`,
                {
                    headers: {
                        Authorization: 'internal',
                        domain,
                    },
                }
            );

            const data = response.data?.data || response.data;
            if (data) {
                // Store in localStorage for persistence
                localStorage.setItem(BrandingService.getStorageKey(), JSON.stringify(data));
                return data;
            }
            return null;
        } catch (error) {
            console.warn('⚠️ Branding API failed:', error);
            return BrandingService.getCachedBranding();
        }
    },

    /**
     * Gets branding from localStorage cache.
     * @returns {BrandingData | null} The cached branding data.
     */
    getCachedBranding: (): BrandingData | null => {
        const cached = localStorage.getItem(BrandingService.getStorageKey());
        if (cached) {
            try {
                return JSON.parse(cached);
            } catch (error_) {
                return null;
            }
        }
        return null;
    },

    /**
     * Gets the active currency code from cached branding data, defaulting to 'SAR'.
     * @returns {string} The active currency code.
     */
    getActiveCurrency: (): string => {
        const brandData = BrandingService.getCachedBranding();
        return brandData?.currencyCode || 'SAR';
    },

    /**
     * Clears branding cache.
     */
    clearCache: () => {
        localStorage.removeItem(BrandingService.getStorageKey());
    },

    /**
     * Resets the favicon to the default state.
     */
    resetFavicon: () => {
        const existingIcons = document.querySelectorAll("link[rel*='icon']");
        existingIcons.forEach((icon) => icon.parentNode?.removeChild(icon));

        const defaultLink = document.createElement('link');
        defaultLink.rel = 'icon';
        defaultLink.type = 'image/png';
        defaultLink.href = '/favicon.ico';
        document.head.appendChild(defaultLink);
    },
};

export default BrandingService;
